import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Box,
} from '@mui/material';
import {
  coerceContextValue,
  type ReferencedContextKey,
} from '../../utils/taskListContextRequirements';

export interface TaskListContextValues {
  fieldValues: Record<string, string | number | boolean>;
  customerContextValues: Record<string, string | number | boolean>;
}

interface TaskListContextPromptDialogProps {
  open: boolean;
  taskListLabel: string;
  missingFieldValueKeys: ReferencedContextKey[];
  missingCustomerContextKeys: ReferencedContextKey[];
  onCancel: () => void;
  onSubmit: (values: TaskListContextValues) => void;
}

type EntryKind = 'field' | 'customer';

interface Entry {
  key: string;
  kind: EntryKind;
  hint: ReferencedContextKey['hint'];
}

type InputState = string | boolean;

export const TaskListContextPromptDialog: React.FC<TaskListContextPromptDialogProps> = ({
  open,
  taskListLabel,
  missingFieldValueKeys,
  missingCustomerContextKeys,
  onCancel,
  onSubmit,
}) => {
  const entries = useMemo<Entry[]>(
    () => [
      ...missingFieldValueKeys.map(r => ({ key: r.key, kind: 'field' as const, hint: r.hint })),
      ...missingCustomerContextKeys.map(r => ({ key: r.key, kind: 'customer' as const, hint: r.hint })),
    ],
    [missingFieldValueKeys, missingCustomerContextKeys],
  );

  const [inputs, setInputs] = useState<Record<string, InputState>>({});

  useEffect(() => {
    if (!open) return;
    const initial: Record<string, InputState> = {};
    for (const e of entries) {
      if (e.hint.kind === 'boolean') initial[e.key] = false;
      else initial[e.key] = '';
    }
    setInputs(initial);
  }, [open, entries]);

  const allFilled = entries.every(({ key, hint }) => {
    const v = inputs[key];
    if (hint.kind === 'boolean') return typeof v === 'boolean';
    return typeof v === 'string' && v.trim() !== '';
  });

  const handleSubmit = () => {
    if (!allFilled) return;
    const fieldValues: Record<string, string | number | boolean> = {};
    const customerContextValues: Record<string, string | number | boolean> = {};
    for (const { key, kind, hint } of entries) {
      const raw = inputs[key];
      let value: string | number | boolean;
      if (hint.kind === 'boolean') {
        value = typeof raw === 'boolean' ? raw : false;
      } else if (hint.kind === 'enum') {
        const matched = hint.options.find(o => String(o) === String(raw));
        value = matched ?? coerceContextValue(String(raw));
      } else {
        value = coerceContextValue(String(raw));
      }
      if (kind === 'field') fieldValues[key] = value;
      else customerContextValues[key] = value;
    }
    onSubmit({ fieldValues, customerContextValues });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Context needed for "{taskListLabel}"</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This task list has conditions that depend on values not yet captured
          on the ticket. Provide them so the right tasks are shown.
        </DialogContentText>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {entries.map(({ key, kind, hint }) => {
            const helper = kind === 'customer' ? 'Customer context' : 'Field value';
            const raw = inputs[key];
            if (hint.kind === 'boolean') {
              const checked = typeof raw === 'boolean' ? raw : false;
              return (
                <Box key={`${kind}:${key}`}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={e => setInputs(prev => ({ ...prev, [key]: e.target.checked }))}
                      />
                    }
                    label={key}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    {helper}
                  </Typography>
                </Box>
              );
            }
            if (hint.kind === 'enum') {
              const value = typeof raw === 'string' ? raw : '';
              return (
                <TextField
                  key={`${kind}:${key}`}
                  select
                  label={key}
                  helperText={helper}
                  value={value}
                  onChange={e => setInputs(prev => ({ ...prev, [key]: e.target.value }))}
                  required
                  fullWidth
                  size="small"
                >
                  {hint.options.map(opt => (
                    <MenuItem key={String(opt)} value={String(opt)}>
                      {String(opt)}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }
            const value = typeof raw === 'string' ? raw : '';
            return (
              <TextField
                key={`${kind}:${key}`}
                label={key}
                helperText={helper}
                value={value}
                onChange={e => setInputs(prev => ({ ...prev, [key]: e.target.value }))}
                required
                fullWidth
                size="small"
              />
            );
          })}
          {entries.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No additional context required.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!allFilled}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
